import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Save, Loader2, BadgePercent } from "lucide-react";
import { toast } from "sonner";

/**
 * Per-vendor length-of-rental discount tiers. Each tier = {min_days, percent}. Applies
 * to the rental_subtotal of every booking on every car this vendor owns. Backend picks
 * the highest applicable tier; ties broken by larger min_days.
 *
 * UI keeps tiers sorted ASC by min_days so the table reads naturally (5d, 7d, 14d, …).
 * Backed by the existing `PATCH /api/users/me/business` endpoint which now accepts
 * `discount_tiers: [{min_days, percent}]`.
 */
const sortTiers = (arr) =>
  [...arr].sort((a, b) => (a.min_days || 0) - (b.min_days || 0));

export default function VendorDiscountTiers() {
  const { user, refresh } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTiers(sortTiers(Array.isArray(user?.discount_tiers) ? user.discount_tiers : []));
  }, [user?.discount_tiers]);

  const addRow = () => setTiers((arr) => [...arr, { min_days: 5, percent: 5 }]);
  const removeRow = (i) => setTiers((arr) => arr.filter((_, idx) => idx !== i));
  const update = (i, field, val) => {
    setTiers((arr) => arr.map((t, idx) => idx === i ? { ...t, [field]: val } : t));
  };

  const save = async () => {
    // Normalise + validate locally so the user sees errors immediately.
    const normalised = [];
    for (const t of tiers) {
      const md = parseInt(t.min_days, 10);
      const pct = parseFloat(t.percent);
      if (!Number.isFinite(md) || md < 1 || md > 365) { toast.error("Each min_days must be 1–365"); return; }
      if (!Number.isFinite(pct) || pct <= 0 || pct > 50) { toast.error("Each percent must be 0 < % ≤ 50"); return; }
      normalised.push({ min_days: md, percent: pct });
    }
    // Detect duplicate min_days
    const seen = new Set();
    for (const t of normalised) {
      if (seen.has(t.min_days)) { toast.error(`Duplicate tier at ${t.min_days} days — combine or remove one.`); return; }
      seen.add(t.min_days);
    }
    setSaving(true);
    try {
      await api.patch("/users/me/business", { discount_tiers: sortTiers(normalised) });
      await refresh();
      toast.success("Discount tiers saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-3xl border border-border bg-white p-6 mt-6" data-testid="vendor-discount-tiers">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-9 w-9 rounded-2xl bg-rose-50 flex items-center justify-center shrink-0"><BadgePercent className="h-4 w-4 text-rose-700" /></div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg font-medium">Length-of-rental discounts</h3>
          <p className="text-xs text-neutral-500 mt-0.5">Reward longer rentals automatically. Higher tiers always win — e.g. with 5d:−5% and 7d:−10%, a 7-day rental gets the 10% off. Applies to the rental cost only (not insurance, add-ons, or deposit).</p>
        </div>
      </div>

      {tiers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-neutral-500" data-testid="discount-tiers-empty">
          No discount tiers yet. Add one to encourage longer rentals.
        </div>
      ) : (
        <div className="space-y-2" data-testid="discount-tiers-list">
          {tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-2 rounded-xl border border-border bg-neutral-50/40 px-3 py-2" data-testid={`discount-tier-row-${i}`}>
              <span className="text-xs text-neutral-500 w-24">Rentals of</span>
              <Input
                type="number" min={1} max={365}
                value={t.min_days ?? ""}
                onChange={(e) => update(i, "min_days", e.target.value)}
                className="w-20 rounded-lg bg-white"
                data-testid={`discount-tier-days-${i}`}
              />
              <span className="text-sm text-neutral-700">days+ get</span>
              <Input
                type="number" min={0} max={50} step={0.5}
                value={t.percent ?? ""}
                onChange={(e) => update(i, "percent", e.target.value)}
                className="w-20 rounded-lg bg-white"
                data-testid={`discount-tier-percent-${i}`}
              />
              <span className="text-sm text-neutral-700">% off</span>
              <div className="flex-1" />
              <Button type="button" size="icon" variant="ghost" onClick={() => removeRow(i)} className="rounded-full text-rose-600 hover:text-rose-700" data-testid={`discount-tier-remove-${i}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
        <Button type="button" variant="ghost" onClick={addRow} className="rounded-full" data-testid="discount-tier-add">
          <Plus className="h-3.5 w-3.5 mr-1.5" />Add tier
        </Button>
        <Button onClick={save} disabled={saving} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="discount-tiers-save">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save tiers
        </Button>
      </div>
    </div>
  );
}
